/**
 * PRODUTORES — o cadastro que preenche a origem sozinho nas próximas fichas.
 * Mudar o cadastro não reescreve romaneio já feito: cada ficha guarda a cópia do dia.
 */
import { useState } from 'react'
import { acharProdutorPeloNome, guardarProdutor, mudar, usarEstado } from '../dados/armazem'
import { agora, dataBR, normalizar, num } from '../dominio/numeros'
import type { Produtor } from '../dominio/tipos'
import { avisar, Campo, Dica, Janela } from '../ui'
import { usarPode } from '../dados/sessao'

export function Produtores() {
  const { produtores, fichas } = usarEstado()
  const [busca, setBusca] = useState('')
  const [editando, setEditando] = useState<Produtor | null>(null)
  // 3E: a Consulta vê o cadastro só para leitura; o comprador muda os dele
  // (no aparelho dele só chegam os produtores dele — 001 produtores_ver)
  const pode = usarPode()
  const podeEditar = editando ? (editando.id ? pode('editarProdutor') : pode('criarProdutor')) : false
  const q = normalizar(busca)
  const lista = produtores.filter((p) => p.ativo !== false).filter((p) => !q || normalizar([p.nome, p.propriedade, p.municipio].join(' ')).includes(q))
    .sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR'))

  function salvar() {
    if (!editando) return
    if (!editando.nome.trim()) { window.alert('Informe o nome.'); return }
    let repetido = false
    mudar((e) => {
      const outro = acharProdutorPeloNome(e, editando.nome)
      if (outro && outro.id !== editando.id) { repetido = true; return }
      guardarProdutor(e, editando)
    })
    if (repetido) { window.alert('Já existe um produtor com esse nome no cadastro.'); return }
    setEditando(null); avisar('Produtor salvo')
  }

  const campo = (k: keyof Produtor, rotulo: string, extra: Partial<Parameters<typeof Campo>[0]> = {}) =>
    editando && <Campo rotulo={rotulo} valor={String(editando[k] ?? '')} mudar={(v) => setEditando({ ...editando, [k]: v })} {...extra} />

  return (
    <div>
      <div className="cartao">
        <input type="search" value={busca} onChange={(e) => setBusca(e.target.value)} placeholder="Buscar nome, propriedade, município" />
        {pode('criarProdutor') && <button className="botao" onClick={() => setEditando({ id: '', nome: '', doc: '', ie: '', propriedade: '', endereco: '', municipio: '', uf: '', telefone: '', gps: null, alteradoEm: '' })}>Novo produtor</button>}
      </div>
      <div className="cartao">
        {lista.length ? lista.map((p) => {
          const deles = fichas.filter((f) => f.produtor.id === p.id && f.situacao !== 'cancelada')
          const ultima = deles.map((f) => f.data).sort().pop()
          return (
            <div key={p.id} className="item clicavel" onClick={() => setEditando({ ...p })}>
              <div className="item-topo"><span className="item-titulo">{p.nome}
                {/* 3D: os marcadores dizem em que lista de porteira o nome aparece */}
                {p.cliente && <> <span className="etiqueta neutra">cliente</span></>}
                {p.meuLocal && <> <span className="etiqueta neutra">meu local</span></>}</span><span className="mini-dica">{deles.length} ficha{deles.length === 1 ? '' : 's'}</span></div>
              <div className="item-baixo">
                <span>{[p.propriedade, [p.municipio, p.uf].filter(Boolean).join('/')].filter(Boolean).join(' · ') || '—'}{p.gps ? ' · 📍' : ''}</span>
                <span>{ultima ? 'última ' + dataBR(ultima) : ''}</span>
              </div>
            </div>
          )
        }) : <div className="vazio">{produtores.length ? 'Nada encontrado.' : 'Os produtores entram aqui sozinhos ao começar uma pesagem.'}</div>}
      </div>

      {editando && (
        <Janela titulo={editando.id ? 'Produtor' : 'Novo produtor'} fechar={() => setEditando(null)}>
          <fieldset disabled={!podeEditar} className="sem-borda">
          {campo('nome', 'Nome')}
          <div className="linha">{campo('doc', 'CPF/CNPJ', { modo: 'numeric' })}{campo('ie', 'Inscrição estadual')}</div>
          {campo('propriedade', 'Propriedade')}
          {campo('endereco', 'Endereço / como chegar')}
          <div className="linha"><div style={{ flex: 3 }}>{campo('municipio', 'Município')}</div>{campo('uf', 'UF', { max: 2, maiusculas: true })}</div>
          {campo('telefone', 'Telefone', { modo: 'tel' })}
          {/* R2B: para pagar sem procurar o papel — aparece na tela Contas, ao lado do que falta pagar */}
          <div className="mini-dica">Para pagar (opcional)</div>
          {campo('pix', 'Chave PIX')}
          <div className="linha">{campo('banco', 'Banco')}{campo('agencia', 'Agência', { modo: 'numeric' })}{campo('conta', 'Conta')}</div>
          {/* 3D: as listas de nome da porteira (Negociação) saem destes dois marcadores */}
          <label className="campo" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <input type="checkbox" style={{ width: 'auto' }} checked={!!editando.cliente} onChange={(e) => setEditando({ ...editando, cliente: e.target.checked })} />
            <span>Cliente (aparece na porteira «Cliente»)</span>
          </label>
          <label className="campo" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <input type="checkbox" style={{ width: 'auto' }} checked={!!editando.meuLocal} onChange={(e) => setEditando({ ...editando, meuLocal: e.target.checked })} />
            <span>Local meu — sítio, fazenda de recria (aparece na porteira «Meu estoque»)</span>
          </label>
          </fieldset>
          {editando.gps && <div className="mini-dica">📍 {num(editando.gps.lat, 5)}, {num(editando.gps.lon, 5)} ·{' '}
            <a href={`https://www.google.com/maps?q=${editando.gps.lat},${editando.gps.lon}`} target="_blank" rel="noreferrer">ver no mapa</a></div>}
          {editando.id && podeEditar && <Dica>Alterar aqui não muda romaneios já feitos: cada ficha guarda os dados do produtor do dia.</Dica>}
          {!podeEditar && <Dica>Somente consulta: o cadastro é mudado pelo gestor ou pelo comprador.</Dica>}
          {podeEditar && <button className="botao" onClick={salvar}>Salvar</button>}
          {editando.id && podeEditar && (
            <button className="botao perigo" onClick={() => {
              if (!window.confirm(`Excluir ${editando.nome} do cadastro?\nAs fichas dele continuam no histórico.`)) return
              mudar((e) => { const x = e.produtores.find((y) => y.id === editando.id); if (x) { x.ativo = false; x.alteradoEm = agora() } })
              setEditando(null)
            }}>Excluir do cadastro</button>
          )}
          <button className="botao fantasma" onClick={() => setEditando(null)}>Fechar</button>
        </Janela>
      )}
    </div>
  )
}
